import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const Footer = () => {
  const { t, isRTL } = useLanguage();

  const quickLinks = [
    { to: '/teachers', label: t('nav.teachers') },
    { to: '/courses', label: t('nav.courses') },
    { to: '/register/teacher', label: isRTL ? 'كن معلمًا' : 'Become a Teacher' },
    { to: '/about', label: t('nav.about') },
    { to: '/blog', label: isRTL ? 'المدونة' : 'Blog' },
    { to: '/faq', label: isRTL ? 'الأسئلة الشائعة' : 'FAQ' },
  ];

  const legalLinks = [
    { to: '/terms', label: isRTL ? 'شروط الاستخدام' : 'Terms of Service' },
    { to: '/privacy', label: isRTL ? 'سياسة الخصوصية' : 'Privacy Policy' },
  ];

  return (
    <footer className="bg-slate-900 dark:bg-slate-950 text-gray-300 relative overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Decorative top border */}
      <div className="h-1 bg-gold-gradient" />

      {/* Islamic pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23C8A84E' stroke-width='0.5'%3E%3Cpath d='M40 0L80 40L40 80L0 40Z'/%3E%3Cpath d='M40 15L65 40L40 65L15 40Z'/%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '80px 80px',
        }} />
      </div>

      <div className="container relative z-10 pt-14 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-9 h-9 rounded-lg bg-amber-400/20 flex items-center justify-center">
                <span lang="ar" className="text-amber-400 font-arabic text-2xl font-bold leading-none">هـ</span>
              </div>
              <span className="font-semibold text-white text-lg">
                {isRTL ? <span lang="ar">هُدًى لِّلْمُتَّقِينَ</span> : 'Hudan Lil Muttaqeen'}
              </span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-5">
              {t('footer.description')}
            </p>
            {/* Quranic verse */}
            <div className="border-s-2 border-amber-400/40 ps-3">
              <p lang="ar" className="font-arabic text-amber-400/80 text-sm leading-relaxed" dir="rtl">
                ذَٰلِكَ الْكِتَابُ لَا رَيْبَ ۛ فِيهِ ۛ هُدًى لِّلْمُتَّقِينَ
              </p>
              <p className="text-gray-500 text-xs mt-1">
                {isRTL ? 'البقرة ٢:٢' : 'Al-Baqarah 2:2'}
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-white text-sm uppercase tracking-wider mb-4">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-2.5 list-none p-0">
              {quickLinks.map(link => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-gray-400 hover:text-amber-400 text-sm transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-semibold text-white text-sm uppercase tracking-wider mb-4">
              {isRTL ? 'قانوني' : 'Legal'}
            </h3>
            <ul className="space-y-2.5 list-none p-0">
              {legalLinks.map(link => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-gray-400 hover:text-amber-400 text-sm transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-white text-sm uppercase tracking-wider mb-4">
              {t('footer.contactUs')}
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-400/60" />
                <span className="text-gray-400 text-sm">
                  {isRTL ? 'الشارقة، الإمارات العربية المتحدة' : 'Sharjah, United Arab Emirates'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 flex-shrink-0 text-amber-400/60" />
                <span className="text-gray-400 text-sm force-ltr">+971 4 123 4567</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 flex-shrink-0 text-amber-400/60" />
                <span className="text-gray-400 text-sm force-ltr">info@hudanlilmuttaqeen.com</span>
              </div>
            </div>

            {/* Social Icons */}
            <div className="flex gap-2 mt-5">
              {['facebook', 'twitter', 'instagram', 'youtube'].map(social => {
                const label = social.charAt(0).toUpperCase() + social.slice(1);
                return (
                  <a
                    key={social}
                    href="#"
                    className="w-9 h-9 rounded-lg bg-white/5 hover:bg-amber-400/15 hover:text-amber-400 flex items-center justify-center text-gray-500 transition-all duration-200"
                    aria-label={isRTL ? `تابع هُدًى على ${label}` : `Follow Hudan on ${label}`}
                  >
                    <SocialIcon name={social} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-gray-500 text-xs">
            &copy; {new Date().getFullYear()} {isRTL ? <span lang="ar">هُدًى لِّلْمُتَّقِينَ</span> : 'Hudan Lil Muttaqeen'}. {t('footer.rights')}
          </p>
          <p lang="ar" className="text-gray-600 text-xs font-arabic" dir="rtl">
            بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
          </p>
        </div>
      </div>
    </footer>
  );
};

const SocialIcon = ({ name }: { name: string }) => {
  const icons: Record<string, React.ReactNode> = {
    facebook: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
      </svg>
    ),
    twitter: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
    instagram: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    ),
    youtube: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  };
  return <>{icons[name]}</>;
};

export default Footer;
